/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Alert, Button, Checkbox, ConfirmModal, PageContainer, PageHeader, Toast } from '@/components/ui'
import { useApi } from '@/hooks'
import useToast from '@/hooks/useToast'
import { insightName } from '@/insights/insightCatalogue'
import logger from '@/utils/logger'

import { LoadingPanel } from '../LoadingPanel'
import {
  APP_GRANT_LABELS,
  ModeSelector,
  PermissionsEditor,
  SelectedNamesSummary,
  UnsavedChangesNote,
  buildUsage,
  disabledInUse,
  normaliseValue,
  usePermissionsForm,
  usersAndDashboardsLabel,
  type AdminCustomerDetail,
  type AdminCustomerSummary,
  type AdminCustomerUser,
  type AppGrant,
  type BulkMode,
  type DisabledSet,
  type PermissionsValue,
  type UsageIndex,
} from '../permissions'

const log = logger({ category: 'AdminCompaniesBulkPage' })

interface BulkState {
  customerIds?: number[]
}

export interface CustomersAccessBody {
  mode: BulkMode
  customerIds: number[]
  grants: AppGrant[]
  insights: string[]
  /** Also apply the same change to every user of each company. */
  applyToUsers?: boolean
}

interface CustomersAccessResult {
  updated: number[]
  missing: number[]
  usersUpdated?: number
}

const EMPTY: PermissionsValue = { grants: [], insights: [] }

/** What the operation would take away from a user who has it on a dashboard. */
const takenAway = (mode: BulkMode, value: PermissionsValue, usage: UsageIndex): DisabledSet => {
  if (mode === 'add') return { grants: [], insights: [] }
  if (mode === 'remove') {
    return {
      grants: value.grants.filter((grant) => usage.inUse(grant)),
      insights: value.insights.filter((slug) => usage.inUse(slug)),
    }
  }
  // Replace: anything in use that is left unchecked stops working for those companies' users.
  return disabledInUse({ grants: [], insights: [...usage.slugs] }, value, usage)
}

const plural = (n: number, noun: string) => `${String(n)} ${noun}${n === 1 ? '' : 's'}`
const companies = (n: number) => `${String(n)} ${n === 1 ? 'company' : 'companies'}`

/** The one-line summary of what Apply will do, by mode. */
const modeSentence = (mode: BulkMode, value: PermissionsValue): string => {
  if (mode === 'replace') return 'All permissions will be replaced.'
  const what = `${plural(value.grants.length, 'app')} and ${plural(value.insights.length, 'insight')}`
  return mode === 'add' ? `${what} will be added.` : `${what} will be removed.`
}

interface BulkConfirmBodyProps {
  count: number
  mode: BulkMode
  value: PermissionsValue
  usage: UsageIndex
  /** Users that will get the same change; 0 when the box is not ticked. */
  usersAffected: number
}

const BulkConfirmBody: React.FC<BulkConfirmBodyProps> = ({ count, mode, value, usage, usersAffected }) => {
  const taken = takenAway(mode, value, usage)
  const lines = [
    ...taken.grants.map((grant) => ({ key: grant, name: APP_GRANT_LABELS[grant] })),
    ...taken.insights.map((slug) => ({ key: slug, name: insightName(slug) })),
  ]
  return (
    <div data-testid="bulk-confirm">
      <p className="text-sm">{companies(count)} will be updated.</p>
      <p className="mt-1 text-sm">{modeSentence(mode, value)}</p>
      {usersAffected > 0 && (
        <p className="mt-1 text-sm">Their {plural(usersAffected, 'user')} will get the same change.</p>
      )}
      {lines.length > 0 && (
        <>
          <p className="mt-3 mb-1 text-sm">
            The following are in use on a dashboard by these companies’ users and will stop working:
          </p>
          <ul className="list-disc pl-5 text-sm">
            {lines.map((line) => (
              <li key={line.key}>
                {line.name} ({usersAndDashboardsLabel(usage.forSlug(line.key))})
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * One permissions edit applied to every company picked on the companies list. The
 * selection arrives via router state; a refresh loses it and lands back on the list.
 */
const AdminCompaniesBulkPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { api } = useApi()
  const { toast, show, showToast, hideToast } = useToast()
  const customerIds = useMemo(() => (location.state as BulkState | null)?.customerIds ?? [], [location.state])

  const [selected, setSelected] = useState<AdminCustomerSummary[] | null>(null)
  const [users, setUsers] = useState<AdminCustomerUser[]>([])
  const [loadError, setLoadError] = useState('')
  const [mode, setMode] = useState<BulkMode>('replace')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [applyToUsers, setApplyToUsers] = useState(false)

  // The editor always starts empty: the checked set IS the operation, in every mode.
  const { value, setValue, isDirty, reset } = usePermissionsForm<PermissionsValue>({
    baseline: EMPTY,
    normalise: normaliseValue,
  })
  const form = value ?? EMPTY

  useEffect(() => {
    if (customerIds.length === 0) return
    let cancelled = false
    const load = async () => {
      try {
        const list = await api.get<AdminCustomerSummary[]>({
          path: '/app/admin/customers',
          origin: 'AdminCompaniesBulkPage#load',
        })
        if (cancelled) return
        if (list.error) {
          setLoadError(list.message || 'Could not load companies')
          return
        }
        const rows = (list.data ?? []).filter((c) => customerIds.includes(c.customerId))
        setSelected(rows)

        // The "in use" counts come from each company's users and their dashboards.
        const details = await Promise.all(
          rows.map((c) =>
            api.get<AdminCustomerDetail>({
              path: `/app/admin/customers/${String(c.customerId)}`,
              origin: 'AdminCompaniesBulkPage#usage',
            })
          )
        )
        if (cancelled) return
        setUsers(details.flatMap((d) => (d.error || !d.data ? [] : d.data.users)))
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setLoadError('Could not load companies')
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, customerIds])

  const usage = useMemo(() => buildUsage(users), [users])
  const totalUsers = useMemo(() => (selected ?? []).reduce((sum, c) => sum + c.userCount, 0), [selected])

  const changeMode = (next: BulkMode) => {
    setMode(next)
    reset()
    setSaveError('')
  }

  const save = useCallback(async () => {
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.put<CustomersAccessResult, CustomersAccessBody>({
        path: '/app/admin/customers/access',
        origin: 'AdminCompaniesBulkPage#save',
        body: {
          mode,
          customerIds,
          grants: form.grants,
          insights: form.insights,
          ...(applyToUsers ? { applyToUsers: true } : {}),
        },
      })
      if (response.error || !response.data) {
        setSaveError(response.message || 'Could not save')
        return
      }
      const { updated, missing, usersUpdated = 0 } = response.data
      showToast({
        header: `Updated ${companies(updated.length)}${
          missing.length > 0 ? `, ${String(missing.length)} not found` : ''
        }${usersUpdated > 0 ? `; ${plural(usersUpdated, 'user')} updated` : ''}`,
        body: 'Caps apply to each company’s users at their next sign-in or token refresh.',
        position: 'end',
        success: true,
      })
      navigate('/admin/companies')
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }, [api, applyToUsers, customerIds, form, mode, navigate, showToast])

  const handleSave = () => setConfirming(true)
  const handleConfirm = () => {
    setConfirming(false)
    void save()
  }

  if (customerIds.length === 0) {
    return <Navigate to="/admin/companies" replace />
  }

  const count = selected?.length ?? 0
  const subtitle = selected === null ? undefined : `${companies(count)} selected`

  return (
    <PageContainer>
      <Link to="/admin/companies" className="link link-hover mb-2 inline-block text-sm">
        ← Companies
      </Link>
      <PageHeader title="Set company permissions" subtitle={subtitle} />

      {loadError && <Alert message={loadError} type="error" />}

      {selected === null && !loadError ? (
        <LoadingPanel label="Loading companies…" />
      ) : (
        <section className="card bg-base-100 shadow">
          <div className="card-body">
            <SelectedNamesSummary
              noun="companies"
              data-testid="selected-companies"
              items={(selected ?? []).map((c) => ({
                key: String(c.customerId),
                label: c.name,
                title: `customer ${String(c.customerId)}`,
              }))}
            />

            <div data-testid="cap-note">
              <Alert
                type="info"
                message="Company permissions cap their users; existing users keep their own lists — use the users bulk update to change those."
              />
            </div>

            <ModeSelector value={mode} onChange={changeMode} disabled={isSaving} subject="companies" />

            <PermissionsEditor
              value={form}
              onChange={(next) => setValue(next)}
              cap={null}
              usage={usage}
              inUseLabel={usersAndDashboardsLabel}
              disabled={isSaving}
            />

            {saveError && <Alert message={saveError} type="error" />}

            {/* Pushes the same change onto each company's users, in the chosen mode. */}
            <div className="mt-4">
              <Checkbox
                id="apply-to-users"
                data-testid="apply-to-users"
                label={`Apply to all ${plural(totalUsers, 'user')} of the selected companies`}
                checked={applyToUsers}
                disabled={totalUsers === 0 || isSaving}
                onChange={setApplyToUsers}
              />
            </div>

            <div className="card-actions mt-4 justify-end">
              <Button handleClick={() => navigate('/admin/companies')} isDisabled={isSaving} variation="ghost">
                Cancel
              </Button>
              <Button handleClick={handleSave} isDisabled={!isDirty || isSaving || count === 0} variation="primary">
                {isSaving ? (
                  <>
                    <span className="loading loading-spinner loading-xs" />
                    Saving…
                  </>
                ) : (
                  `Apply to ${companies(count)}`
                )}
              </Button>
            </div>
            <UnsavedChangesNote show={isDirty} />

            {/* Always confirm: this writes to every selected company at once. The shared Modal keeps
                its body mounted while closed, so only mount it while there is a question. */}
            {confirming && (
              <ConfirmModal
                show
                title={`Apply to ${companies(count)}?`}
                variation="warning"
                confirmLabel="Apply"
                cancelLabel="Keep editing"
                isLoading={isSaving}
                onConfirm={handleConfirm}
                onCancel={() => setConfirming(false)}
                body={
                  <BulkConfirmBody
                    count={count}
                    mode={mode}
                    value={form}
                    usage={usage}
                    usersAffected={applyToUsers ? totalUsers : 0}
                  />
                }
              />
            )}
          </div>
        </section>
      )}
      <Toast
        show={show}
        onClose={hideToast}
        header={toast.header}
        body={toast.body}
        position={toast.position}
        success={toast.success}
        info={toast.info}
        warning={toast.warning}
      />
    </PageContainer>
  )
}

export default AdminCompaniesBulkPage
